import { TCommonOption, TRowComponentProps, TWidget } from '@libs/types';
import clsx from 'clsx';
import React from 'react';
import { TConsumerItem, TConsumerListData } from '../../../types/data';
import { TRowOption } from '../../../types/options';
import { getInlineStyles } from '../../../utils/styles';
import { Visual } from '../../Image/Visual';
import { Tag } from '../Tag/Tag';
import { TextCommon } from '../Text/TextCommon';
import { TRowComponent } from './RowComponent';
import styles from './styles/rowRegular.module.css';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';

export type TRowRegular = {
    uid?: TWidget<TCommonOption>['uid'];
} & Omit<TRowComponentProps, 'id' | 'value'> &
    Omit<TConsumerItem, 'id' | 'value'> &
    Pick<TCommonOption, 'margin'> &
    Pick<TRowComponent, 'handleClick'>;

export function RowRegular(props: TRowRegular) {
    const finalWidget = {
        uid: props.uid ?? '',
        data: {
            title: props.title,
            subtitle: props.subtitle,
            metaText: props.metaText,
        },
        options: {
            isDisabled: props.isDisabled ?? false,
            isHide: props.isHide ?? false,
            metaVariant: props.metaVariant,
            metaHierarchy: props.metaHierarchy,
            iconName: props.iconName,
            flagIcon: props.flagIcon,
            avatar: props.avatar,
            iconHierarchy: props.iconHierarchy,
            iconVariant: props.iconVariant,
            margin: props.margin,
        },
    } as TWidget<TConsumerListData, TRowOption>;
    const isDisabled = finalWidget.options?.isDisabled;

    return (
        <button
            data-test={finalWidget.uid}
            className={clsx(styles.commonRowRegular, isDisabled && styles.disabled)}
            style={getInlineStyles({ margin: finalWidget.options.margin })}
            onClick={props.handleClick}
            aria-hidden={finalWidget.options?.isHide}
            aria-label={finalWidget.data?.title}
            aria-disabled={isDisabled}
            disabled={isDisabled}
        >
            {finalWidget.options?.iconName && (
                <Visual
                    isWidget={false}
                    iconName={finalWidget.options?.iconName}
                    rightMargin
                    iconHierarchy={finalWidget.options?.iconHierarchy}
                    iconVariant={finalWidget.options?.iconVariant}
                    size={'small'}
                    isDisabled={isDisabled}
                />
            )}
            {finalWidget.options?.avatar && <Visual isWidget={false} rightMargin avatar={finalWidget.options?.avatar} size={'small'} isDisabled={isDisabled} />}
            {finalWidget.options?.flagIcon && (
                <Visual isWidget={false} rightMargin flagIcon={finalWidget.options?.flagIcon} size={'small'} isDisabled={isDisabled} />
            )}
            <div className={styles.rowContent}>
                {finalWidget.data?.title && (
                    <div className={clsx(styles.highlightedTitle, isDisabled && styles.disabled)}>{parse(DOMPurify.sanitize(finalWidget.data?.title))}</div>
                )}
                {finalWidget.data?.subtitle && (
                    <TextCommon
                        isWidget={false}
                        content={finalWidget.data?.subtitle}
                        as={'span'}
                        options={{
                            variant: 'secondary',
                            textColor: isDisabled ? 'grey_40_lc' : 'grey_72_lc',
                            margin: { top: 4 },
                        }}
                    />
                )}
            </div>
            {finalWidget.data?.metaText && (
                <Tag
                    metaText={finalWidget.data?.metaText}
                    metaVariant={finalWidget.options?.metaVariant}
                    metaHierarchy={finalWidget.options?.metaHierarchy}
                    disabled={isDisabled}
                />
            )}
        </button>
    );
}
